import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { SearchIcon } from "@repo/ui-parkmobile/icons/SearchIcon";

const searchFieldContainer = tv({
  base: "uipm:relative uipm:w-full uipm:flex uipm:items-stretch",
});

const searchInput = tv({
  base: cn(
    "uipm:flex-1 uipm:rounded-l-xl",
    "uipm:px-4 uipm:py-4 uipm:min-h-[56px]",
    "uipm:text-base uipm:leading-[24px]",
    "uipm:text-[var(--typography-colors-text-body)] uipm:placeholder:text-[var(--typography-colors-text-faded)]",
    "uipm:border-2 uipm:border-transparent uipm:transition-colors",
    "uipm:outline-none",
    "uipm:focus:border-[var(--surface-surface-accent)]",
    "uipm:font-['Inter']",
  ),
  variants: {
    state: {
      default: "",
      error: "uipm:border-[var(--color-color-danger)]",
      disabled: "uipm:opacity-50 uipm:cursor-not-allowed",
    },
    background: {
      default: "uipm:bg-[var(--surface-surface-secondary)]",
      white: "uipm:bg-white uipm:hover:bg-[var(--surface-surface-secondary)]",
    },
  },
  defaultVariants: {
    state: "default",
    background: "default",
  },
});

const searchButton = tv({
  base: cn(
    "uipm:flex uipm:items-center uipm:justify-center uipm:px-4 uipm:min-w-[56px]",
    "uipm:bg-[var(--surface-surface-accent)] uipm:rounded-r-xl",
    "uipm:transition-colors uipm:cursor-pointer",
    "uipm:hover:bg-[var(--color-color-accent-hover)]",
    "uipm:active:bg-[var(--color-color-accent-focus)]",
    "uipm:focus:outline-none uipm:focus-visible:ring-2 uipm:focus-visible:ring-[var(--surface-surface-accent)]",
  ),
  variants: {
    state: {
      default: "",
      hover: "uipm:bg-[var(--color-color-accent-hover)]",
      pressed: "uipm:bg-[var(--color-color-accent-focus)]",
      disabled: "uipm:opacity-50 uipm:cursor-not-allowed",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

export type SearchFieldProps = VariantProps<typeof searchInput> &
  Omit<ComponentProps<"input">, "type"> & {
    className?: string;
    containerClassName?: string;
    background?: "default" | "white";
    onSearch?: (value: string) => void;
    searchHref?: string;
    onSearchClick?: () => void;
    searchButtonProps?: ComponentProps<"button">;
    sectionName?: string;
    analyticsEvent?: "search_location" | "";
  };

export function SearchField({
  className,
  containerClassName,
  state,
  background = "default",
  disabled,
  onSearch,
  searchHref,
  onSearchClick,
  searchButtonProps,
  value,
  onChange,
  sectionName, // eslint-disable-line @typescript-eslint/no-unused-vars
  analyticsEvent, // eslint-disable-line @typescript-eslint/no-unused-vars
  ...props
}: SearchFieldProps) {
  const [internalValue, setInternalValue] = React.useState("");
  const inputState = disabled ? "disabled" : state;

  const isControlled = value !== undefined;
  const inputValue = isControlled ? value : internalValue;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (!isControlled) {
      setInternalValue(newValue);
    }

    onChange?.(e);
  };

  const handleSearch = () => {
    if (!disabled) {
      onSearchClick?.();
      onSearch?.(String(inputValue));
    }
  };

  React.useEffect(() => {
    if (!isControlled) {
      setInternalValue(value || "");
    }
  }, [value, isControlled]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (searchHref && !disabled) {
        onSearchClick?.();
        window.open(searchHref, "_blank", "noopener,noreferrer");
      } else {
        handleSearch();
      }
    }
    props.onKeyDown?.(e);
  };

  const buttonClassName = cn(
    searchButton({ state: disabled ? "disabled" : "default" }),
    searchButtonProps?.className,
  );

  return (
    <div className={cn(searchFieldContainer(), containerClassName)}>
      <input
        type="search"
        disabled={disabled}
        {...props}
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        className={cn(
          searchInput({ state: inputState, background }),
          className,
        )}
        placeholder={props.placeholder || "Search by location or venue"}
      />
      {searchHref && !disabled ? (
        <a
          href={searchHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => onSearchClick?.()}
          aria-label="Search"
          className={buttonClassName}
        >
          <SearchIcon size={24} color="white" className="uipm:text-white" />
        </a>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={handleSearch}
          aria-label="Search"
          {...searchButtonProps}
          className={buttonClassName}
        >
          <SearchIcon size={24} color="white" className="uipm:text-white" />
        </button>
      )}
    </div>
  );
}
